import React from 'react';

import Icon, { faInfoCircle } from '~components/icon';

import StackedBudgetBar from './StackedBudgetBar';

interface Service {
  id: number;
  name: string;
  code: string;
  category_id: string;
  category_name?: string;
  allocated: number;
  booked: number;
  claimed: number;
  remaining: number;
  overspend: number;
  utilization_percentage: number;
  units: string;
}

interface Category {
  id: string;
  name: string;
  color?: string;
}

interface ComponentProps {
  services: Service[];
  categories: Category[];
}

const ServiceBreakdown: React.FC<ComponentProps> = ({ services, categories }) => {
  const formatCurrency = (value: number) => {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  // Filter services that have allocation > 0
  const servicesWithAllocation = services.filter((service) => service.allocated > 0);

  // Group services by category
  const servicesByCategory = servicesWithAllocation.reduce(
    (acc, service) => {
      if (!acc[service.category_id]) {
        acc[service.category_id] = [];
      }
      acc[service.category_id].push(service);
      return acc;
    },
    {} as Record<string, Service[]>,
  );

  // Create category lookup map
  const categoryLookup = categories.reduce(
    (acc, category) => {
      acc[category.id] = category;
      return acc;
    },
    {} as Record<string, Category>,
  );

  return (
    <section className="service-breakdown-section">
      <div className="section-card-title">Service Breakdown</div>
      <div className="section-card-description">Individual service budget utilization and projections by category</div>
      <div className="service-breakdown-layout">
        {servicesWithAllocation.length === 0 && (
          <div className="no-data-message">
            <Icon icon={faInfoCircle} />
            No services included in the budget.
          </div>
        )}
        {Object.entries(servicesByCategory).map(([categoryId, categoryServices]) => {
          const category = categoryLookup[categoryId];

          return (
            <div
              key={categoryId}
              className="service-breakdown-category"
              style={
                {
                  '--service-card-border-color': category?.color || '#888',
                } as React.CSSProperties
              }
            >
              <div className="section-card-title">{category?.name || 'Unknown Category'}</div>
              <div className="service-breakdown-services" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                {categoryServices.map((service) => {
                  const utilization = service.booked + service.claimed;
                  const budget = service.allocated;

                  return (
                    <div key={service.code} className="service-breakdown-service">
                      <div className="service-breakdown-service-header">
                        <span className="service-breakdown-service-name">{service.name}</span>
                      </div>
                      <div>
                        <span className="badge service-breakdown-type">{service.code}</span>
                      </div>
                      <div className="service-breakdown-graph">
                        <StackedBudgetBar
                          budget={budget}
                          utilization={utilization}
                          color={category?.color || '#4285f4'}
                          legendTitle="Budget vs Utilization"
                          axisLeftLegend="Amount"
                          valueFormatter={formatCurrency}
                        />
                      </div>
                      <div className="service-breakdown-details">
                        <div className="service-breakdown-totals mt-2">
                          <div className="service-breakdown-total-row">
                            <span className="service-breakdown-label">Total Budget:</span>
                            <span className="service-breakdown-value">{formatCurrency(service.allocated)}</span>
                          </div>
                        </div>
                        {service.overspend > 0 && (
                          <span className="service-breakdown-overspend">
                            Overspend: {formatCurrency(service.overspend)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default ServiceBreakdown;
